import React, { useState, useCallback, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import { FiX, FiCheck, FiInfo } from 'react-icons/fi';
import './ImageCrop.css';

const ASPECT_PRESETS = [
  { id: 'free', label: 'Free', aspect: undefined, desc: 'Any shape' },
  { id: 'square', label: '1:1', aspect: 1, desc: 'US / India' },
  { id: 'eu', label: '35:45', aspect: 35 / 45, desc: 'EU / UK' },
];

/**
 * ImageCrop Component
 * Drag photo to crop from any side; optional passport aspect ratios.
 */
const ImageCrop = ({ imageFile, onCropComplete, onCancel, suggestedAspect, suggestedLabel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [applyError, setApplyError] = useState(null);
  const [applying, setApplying] = useState(false);

  const initialAspectId = useMemo(() => {
    if (suggestedAspect != null) return 'suggested';
    return 'free';
  }, [suggestedAspect]);

  const [aspectId, setAspectId] = useState(initialAspectId);

  const aspectOptions = useMemo(() => {
    const options = [...ASPECT_PRESETS];
    if (suggestedAspect != null) {
      options.unshift({
        id: 'suggested',
        label: 'Recommended',
        aspect: suggestedAspect,
        desc: suggestedLabel || 'Your selected size',
      });
    }
    return options;
  }, [suggestedAspect, suggestedLabel]);

  const activeAspect = aspectOptions.find((o) => o.id === aspectId)?.aspect;

  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  React.useEffect(() => {
    setAspectId(suggestedAspect != null ? 'suggested' : 'free');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setApplyError(null);
  }, [imageFile, suggestedAspect]);

  const onCropChange = useCallback((nextCrop) => {
    setCrop(nextCrop);
    setApplyError(null);
  }, []);

  const onZoomChange = useCallback((nextZoom) => {
    setZoom(nextZoom);
    setApplyError(null);
  }, []);

  const onCropAreaChangeCallback = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleAspectChange = (id) => {
    setAspectId(id);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setApplyError(null);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (src, pixelCrop) => {
    const image = await createImage(src);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas not supported');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to export cropped image'));
            return;
          }
          resolve(
            new File([blob], imageFile.name, {
              type: imageFile.type || 'image/png',
            })
          );
        },
        imageFile.type || 'image/png',
        0.92
      );
    });
  };

  const handleApply = async () => {
    setApplyError(null);

    if (!imageSrc) {
      setApplyError('Image is still loading. Please wait a moment.');
      return;
    }

    if (!croppedAreaPixels) {
      setApplyError('Drag the photo to position it, then try Apply again.');
      return;
    }

    setApplying(true);
    try {
      const croppedImageFile = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedImageFile);
    } catch (error) {
      console.error('Error cropping image:', error);
      setApplyError('Failed to crop image. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  if (!imageSrc) {
    return (
      <div className="crop-modal-overlay">
        <div className="crop-modal">
          <div className="crop-loading">Loading image...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="crop-modal-overlay" onClick={onCancel}>
      <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crop-header">
          <h3>Crop Image</h3>
          <button className="crop-close-btn" onClick={onCancel} aria-label="Close" type="button">
            <FiX />
          </button>
        </div>

        <div className="crop-hint">
          <FiInfo size={14} />
          <span>
            <strong>Drag</strong> the photo to crop from any side. Use <strong>zoom</strong> to focus on the face.
            The frame stays fixed — you move the image inside it.
          </span>
        </div>

        <div className="crop-content-scroll">
          <div className="crop-aspect-row">
            <span className="crop-aspect-label">Aspect ratio</span>
            <div className="crop-aspect-buttons">
              {aspectOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleAspectChange(option.id)}
                  className={`crop-aspect-btn ${aspectId === option.id ? 'active' : ''}`}
                  title={option.desc}
                >
                  <span className="crop-aspect-btn-label">{option.label}</span>
                  <span className="crop-aspect-btn-desc">{option.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="crop-container-wrapper">
            <div className="crop-container">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={activeAspect}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropAreaChange={onCropAreaChangeCallback}
                onCropComplete={onCropAreaChangeCallback}
                cropShape="rect"
                showGrid={true}
                style={{
                  containerStyle: {
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    background: '#000',
                  },
                }}
              />
            </div>
          </div>

          <div className="crop-controls">
            <div className="zoom-control">
              <label htmlFor="crop-zoom">Zoom</label>
              <input
                id="crop-zoom"
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-value">{zoom.toFixed(1)}x</span>
            </div>
          </div>

          {applyError && (
            <div className="crop-error" role="alert">
              {applyError}
            </div>
          )}
        </div>

        <div className="crop-actions">
          <button className="crop-cancel-btn" type="button" onClick={onCancel} disabled={applying}>
            Cancel
          </button>
          <button
            className="crop-apply-btn"
            type="button"
            onClick={handleApply}
            disabled={applying}
          >
            <FiCheck /> {applying ? 'Applying...' : 'Apply Crop'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCrop;
